import { getDocumentFileToBase64 } from "./document";
import { MAX_UPLOAD_BASE64_LENGTH } from "./document-upload-guard";
import type { RedlineFailureStage } from "./redline-failure";

export async function sha256Hex(input: string): Promise<string> {
    const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(input));
    return Array.from(new Uint8Array(digest)).map(byte => byte.toString(16).padStart(2, "0")).join("");
}

export type RedlineSnapshotCapture =
    | {
        status: "captured";
        documentBase64: string;
        base64Length: number;
    }
    | {
        status: "too_large";
        documentDigest: string;
        base64Length: number;
        limitBase64Length: number;
    }
    | {
        status: "capture_failed";
        reason: string;
    }
    | {
        status: "not_captured";
    };

export interface RedlineApplyContext {
    capture: RedlineSnapshotCapture;
    reviewId: string | null;
}

export async function captureRedlineSnapshot(): Promise<RedlineSnapshotCapture> {
    try {
        const documentBase64 = await getDocumentFileToBase64();
        const base64Length = documentBase64.length;

        if (base64Length > MAX_UPLOAD_BASE64_LENGTH) {
            return {
                status: "too_large",
                documentDigest: await sha256Hex(documentBase64),
                base64Length,
                limitBase64Length: MAX_UPLOAD_BASE64_LENGTH,
            };
        }

        return { status: "captured", documentBase64, base64Length };
    } catch (error) {
        return { status: "capture_failed", reason: error instanceof Error ? error.message : String(error) };
    }
}

export interface MintSnapshotIdArgs {
    documentBase64: string;
    attemptedPayload: string;
    stage: RedlineFailureStage;
}

export async function mintSnapshotId({
    documentBase64,
    attemptedPayload,
    stage,
}: MintSnapshotIdArgs): Promise<string> {
    return sha256Hex(`${documentBase64}\n${attemptedPayload}\n${stage}`);
}

export interface StartApplyAttemptArgs {
    capture: RedlineSnapshotCapture;
    reviewId: string | null;
}

export function startApplyAttempt({ capture, reviewId }: StartApplyAttemptArgs): RedlineApplyContext {
    return { capture, reviewId };
}
